import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchGeneratedAds, InsufficientCreditsError, type AdFormData } from '../../api/generateAds';
import { getBulkCreditCost, getEngineConfig } from '../../config/imageEngines';
import { useAuth } from '../../context/AuthContext';
import { useBusinessProfiles } from '../../hooks/useBusinessProfiles';
import { useCredits } from '../../hooks/useCredits';
import type { AdResult } from '../../mocks/adResults';
import { trackEvent } from '../../lib/analytics';
import { supabase } from '../../lib/supabase';

type IndustryKey = 'contractor' | 'real_estate' | 'fitness' | 'local_business';

interface IndustryPreset {
  label: string;
  businessType: string;
  audience: string;
  tone: string;
  campaignGoal: string;
  servicesOffered: string;
  brandPositioning: string;
  uniqueDifferentiator: string;
  promptFocus: string;
  emotionalHook: string;
  offerStructure: string;
  painPoints: string;
  desiredOutcome: string;
  trustValueAngle: string;
  ctaStyle: string;
  platformGuidance: Record<string, string>;
}

const INDUSTRY_PRESETS: Record<IndustryKey, IndustryPreset> = {
  contractor: {
    label: 'Contractor',
    businessType: 'Home Services Contractor',
    audience: 'Local homeowners who need reliable, licensed help for repairs, remodeling, or urgent service',
    tone: 'Trustworthy, direct, local expert',
    campaignGoal: 'Generate leads',
    servicesOffered: 'Estimates, repairs, installs, remodels, emergency service, maintenance',
    brandPositioning: 'A dependable local contractor that responds quickly and does the job right',
    uniqueDifferentiator: 'Licensed pros, clear pricing, and fast local availability',
    promptFocus: 'Show a clean jobsite, confident professional, before-after transformation, and a clear local service offer',
    emotionalHook: 'Relief from the stress of not knowing who to trust with an expensive home problem',
    offerStructure: 'Lead with a free estimate or inspection, add urgency with limited weekly availability, and reduce risk with licensed local expertise',
    painPoints: 'Unclear pricing, slow callbacks, messy work, fear of hiring the wrong contractor',
    desiredOutcome: 'A fixed home problem, a clear next step, and confidence the job will be handled professionally',
    trustValueAngle: 'Licensed, insured, local, responsive, proof-driven, and easy to contact',
    ctaStyle: 'Direct lead CTA such as Book Free Estimate, Get My Inspection, or Call Today',
    platformGuidance: {
      Facebook: 'Use a local proof angle, clear service area language, and a homeowner-friendly lead offer',
      Instagram: 'Make the visual transformation obvious with a clean before-after or finished-project feel',
      Google: 'Be intent-heavy, specific, and practical with service keywords and fast-response language',
      TikTok: 'Lean into satisfying transformation, speed, and simple problem-solution framing',
      LinkedIn: 'Position as a reliable professional vendor for property owners and managers',
    },
  },
  real_estate: {
    label: 'Real Estate',
    businessType: 'Real Estate',
    audience: 'Local buyers, sellers, and homeowners considering a move in the next 90 days',
    tone: 'Polished, confident, helpful',
    campaignGoal: 'Generate leads',
    servicesOffered: 'Home valuations, buyer consultations, listing strategy, neighborhood expertise',
    brandPositioning: 'A market-savvy local real estate expert who helps clients move with confidence',
    uniqueDifferentiator: 'Local market insight and a clear plan for buyers and sellers',
    promptFocus: 'Feature inviting property visuals, aspirational lifestyle cues, and a confident real estate lead magnet',
    emotionalHook: 'Confidence that the next move can be clear, profitable, and less overwhelming',
    offerStructure: 'Offer a free home valuation, buyer consultation, or market snapshot with a low-friction CTA',
    painPoints: 'Uncertainty about home value, timing the market, finding serious buyers, and avoiding costly listing mistakes',
    desiredOutcome: 'A clear plan to buy or sell with confidence, better pricing, and fewer surprises',
    trustValueAngle: 'Local market expertise, strategic guidance, and a low-pressure consultation',
    ctaStyle: 'Consultative CTA such as Get My Home Value, See Local Listings, or Book a Market Review',
    platformGuidance: {
      Facebook: 'Use neighborhood relevance, approachable expert tone, and a simple lead magnet',
      Instagram: 'Use aspirational property imagery, polished lifestyle cues, and concise copy',
      Google: 'Match high-intent searches with valuation, listing, or buyer consultation language',
      TikTok: 'Frame the ad around a quick market insight or surprising local real estate tip',
      LinkedIn: 'Emphasize professionalism, relocation, investment, and market credibility',
    },
  },
  fitness: {
    label: 'Fitness',
    businessType: 'Fitness',
    audience: 'Adults who want accountability, visible progress, and a realistic routine they can stick with',
    tone: 'Motivating, energetic, supportive',
    campaignGoal: 'Boost sales / conversions',
    servicesOffered: 'Personal training, group classes, transformation programs, nutrition coaching',
    brandPositioning: 'A results-focused fitness brand that makes progress feel achievable',
    uniqueDifferentiator: 'Coaching, accountability, and practical programs for real schedules',
    promptFocus: 'Show active, confident people training in a clean modern gym with strong transformation energy',
    emotionalHook: 'The feeling of finally seeing progress with coaching that fits real life',
    offerStructure: 'Start with a trial class, intro session, or challenge, then emphasize accountability and visible momentum',
    painPoints: 'Lack of motivation, inconsistent routines, confusion about what works, and not seeing results',
    desiredOutcome: 'More energy, visible progress, confidence, and a plan that feels sustainable',
    trustValueAngle: 'Coached accountability, beginner-friendly structure, and proof of achievable progress',
    ctaStyle: 'Momentum CTA such as Start My Trial, Join the Challenge, or Book My First Session',
    platformGuidance: {
      Facebook: 'Use transformation and community proof with an easy trial or challenge offer',
      Instagram: 'Prioritize energetic visuals, strong body-language, and short motivational copy',
      Google: 'Use specific program, location, and trial intent language',
      TikTok: 'Make it feel active, relatable, and progress-oriented with a simple challenge hook',
      LinkedIn: 'Focus on wellness, busy schedules, productivity, and sustainable coaching',
    },
  },
  local_business: {
    label: 'Local Business',
    businessType: 'Local Business',
    audience: 'Nearby customers looking for a trusted local option today or this week',
    tone: 'Friendly, clear, community-focused',
    campaignGoal: 'Drive website traffic',
    servicesOffered: 'Local products, appointments, services, promotions, seasonal offers',
    brandPositioning: 'A friendly neighborhood business people can trust and visit easily',
    uniqueDifferentiator: 'Personal service, convenient location, and a timely local offer',
    promptFocus: 'Highlight the storefront or service experience, friendly staff, and a simple neighborhood offer',
    emotionalHook: 'The satisfaction of choosing a local business that feels personal and easy',
    offerStructure: 'Use a simple today-or-this-week offer, clear benefit, and easy next step like book, call, or visit',
    painPoints: 'Too many choices, weak trust signals, unclear value, and no reason to act now',
    desiredOutcome: 'A simple local choice that feels trustworthy, convenient, and worth trying today',
    trustValueAngle: 'Friendly service, community connection, convenience, and a clear timely offer',
    ctaStyle: 'Low-friction CTA such as Visit Today, Claim Offer, Book Now, or Call Us',
    platformGuidance: {
      Facebook: 'Emphasize neighborhood familiarity, friendly proof, and a timely offer',
      Instagram: 'Make the product, place, or experience visually desirable and easy to understand',
      Google: 'Use clear local intent, category terms, and direct action language',
      TikTok: 'Use a fast, personable, behind-the-scenes or customer-experience angle',
      LinkedIn: 'Position around local credibility, service quality, and community trust',
    },
  },
};

const platforms = ['Facebook', 'Instagram', 'Google', 'TikTok', 'LinkedIn'];
const quickStartCost = getBulkCreditCost('standard', 1);
const anonymousGenerationLimit = 3;
const anonymousGenerationKey = 'adforge_quick_start_anonymous_generations';
const anonymousAttemptKey = 'adforge_quick_start_anonymous_attempts';
const progressSteps = ['Writing your ad copy...', 'Designing your image...', 'Finalizing...'];

function firstValue(values?: string[]) {
  return values?.find((value) => value.trim()) ?? '';
}

function getImageSource(result: AdResult | null) {
  if (!result) return '';
  return result.imageUrls?.[0] ?? result.imageStorageUrls?.[0] ?? (result.imageBase64 ? `data:image/png;base64,${result.imageBase64}` : '');
}

function inferIndustryFromIdea(idea: string): IndustryKey {
  const normalized = idea.toLowerCase();
  if (/(roof|plumb|hvac|electric|contract|remodel|landscap|cleaning|repair|home service)/.test(normalized)) return 'contractor';
  if (/(real estate|realtor|home value|listing|buyer|seller|property)/.test(normalized)) return 'real_estate';
  if (/(gym|fitness|workout|trainer|training|yoga|pilates|nutrition)/.test(normalized)) return 'fitness';
  return 'local_business';
}

function inferBusinessNameFromIdea(idea: string): string {
  const trimmed = idea.trim();
  const separatorMatch = trimmed.match(/^(.{2,42}?)(?:\s[-–—:]\s)(.+)$/);
  if (separatorMatch?.[1] && !/\b(discount|sale|free|promo|offer|topic|create|generate)\b/i.test(separatorMatch[1])) {
    return separatorMatch[1].trim();
  }
  const namedMatch = trimmed.match(/\b(?:for|at)\s+([A-Z][A-Za-z0-9&'. ]{2,42})(?:\s+(?:that|to|with|offering|selling|promoting)\b|$)/);
  return namedMatch?.[1]?.trim() ?? '';
}

function inferOfferFromIdea(idea: string): string {
  const trimmed = idea.trim();
  const separatorMatch = trimmed.match(/^(.{2,42}?)(?:\s[-–—:]\s)(.+)$/);
  return (separatorMatch?.[2] ?? trimmed).trim();
}

export default function QuickStartPage() {
  const [searchParams] = useSearchParams();
  const starterIdea = searchParams.get('idea')?.trim() ?? '';
  const { user } = useAuth();
  const { creditsRemaining, loading: creditsLoading, hasEnoughCredits, refetch } = useCredits();
  const { createProfile } = useBusinessProfiles();
  const [businessName, setBusinessName] = useState(() => inferBusinessNameFromIdea(starterIdea));
  const [industry, setIndustry] = useState<IndustryKey>(() => inferIndustryFromIdea(starterIdea));
  const [offer, setOffer] = useState(() => inferOfferFromIdea(starterIdea));
  const [platform, setPlatform] = useState('Facebook');
  const [result, setResult] = useState<AdResult | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [generationCount, setGenerationCount] = useState(0);
  const [savedAdsCount, setSavedAdsCount] = useState(0);
  const [anonymousGenerations, setAnonymousGenerations] = useState(() => {
    if (typeof window === 'undefined') return 0;
    return Number(window.localStorage.getItem(anonymousGenerationKey) ?? '0') || 0;
  });
  const [anonymousAttempts, setAnonymousAttempts] = useState(() => {
    if (typeof window === 'undefined') return 0;
    return Number(window.localStorage.getItem(anonymousAttemptKey) ?? '0') || 0;
  });
  const [error, setError] = useState('');
  const [profileStatus, setProfileStatus] = useState<'idle' | 'saving' | 'saved' | 'skipped' | 'error'>('idle');
  const generationAbortRef = useRef<AbortController | null>(null);

  const preset = INDUSTRY_PRESETS[industry];
  const canGenerate = businessName.trim().length > 1 && offer.trim().length > 2 && platform;
  const missingRequiredFields = [
    businessName.trim().length > 1 ? '' : 'business name',
    offer.trim().length > 2 ? '' : 'offer',
    platform ? '' : 'platform',
  ].filter(Boolean);
  const isAnonymousUser = Boolean(user?.is_anonymous);
  const hasUser = Boolean(user);
  const isAnonymousVisitor = !hasUser || isAnonymousUser;
  const anonymousRemaining = Math.max(0, anonymousGenerationLimit - anonymousGenerations);
  const anonymousLimitReached = isAnonymousVisitor && anonymousRemaining <= 0;
  const adsRemaining = isAnonymousVisitor ? anonymousRemaining : Math.max(0, Math.floor(creditsRemaining / quickStartCost));
  const enoughCredits = !hasUser || hasEnoughCredits(quickStartCost);
  const imageSource = getImageSource(result);

  useEffect(() => {
    trackEvent('quick_start_opened', { source: 'route' });
  }, []);

  useEffect(() => {
    if (!isGenerating) {
      setProgressIndex(0);
      return;
    }

    const timer = window.setInterval(() => {
      setProgressIndex((current) => Math.min(current + 1, progressSteps.length - 1));
    }, 2500);

    return () => window.clearInterval(timer);
  }, [isGenerating]);

  useEffect(() => {
    if (result && isAnonymousVisitor) {
      trackEvent('quick_start_signup_prompt_shown', {
        generation_count: generationCount,
        anonymous_generations: anonymousGenerations,
      });
    }
  }, [anonymousGenerations, generationCount, isAnonymousVisitor, result]);

  useEffect(() => {
    if (!result) return;

    trackEvent('upgrade_prompt_shown', {
      source: generationCount >= 2 ? 'quick_start_second_generation' : 'quick_start_first_generation',
      generation_count: generationCount,
    });
  }, [generationCount, result]);

  useEffect(() => {
    return () => generationAbortRef.current?.abort();
  }, []);

  const platformCreativeGuidance = preset.platformGuidance[platform] ?? preset.platformGuidance.Facebook;

  const payload = useMemo<AdFormData>(() => ({
    businessName: businessName.trim(),
    businessType: preset.businessType,
    businessDescription: `${businessName.trim()} is a ${preset.businessType.toLowerCase()} focused on ${preset.brandPositioning.toLowerCase()}. Audience pain points: ${preset.painPoints}. Desired outcome: ${preset.desiredOutcome}. Trust/value angle: ${preset.trustValueAngle}.`,
    audience: `${preset.audience}. Pain points: ${preset.painPoints}. Desired outcome: ${preset.desiredOutcome}.`,
    offer: offer.trim(),
    platform,
    tone: preset.tone,
    campaignGoal: preset.campaignGoal,
    servicesOffered: preset.servicesOffered,
    brandPositioning: preset.brandPositioning,
    uniqueDifferentiator: `${preset.uniqueDifferentiator}. Trust/value angle: ${preset.trustValueAngle}. Emotional hook: ${preset.emotionalHook}. Offer structure: ${preset.offerStructure}. CTA style: ${preset.ctaStyle}.`,
    adAngle: 'Offer-driven',
    campaignNotes: `Make the output conversion-focused. Audience pain points: ${preset.painPoints}. Desired outcome: ${preset.desiredOutcome}. Trust/value angle: ${preset.trustValueAngle}. CTA style: ${preset.ctaStyle}. Platform guidance for ${platform}: ${platformCreativeGuidance}.`,
    creativeDirection: `${preset.promptFocus}. Target audience: ${preset.audience}. Pain points: ${preset.painPoints}. Desired outcome: ${preset.desiredOutcome}. Emotional hook: ${preset.emotionalHook}. Offer structure: ${preset.offerStructure}. Platform-specific guidance: ${platformCreativeGuidance}. Include visual cues for: ${offer.trim()}.`,
    stylePack: 'auto',
    imageCount: 1,
    imageEngine: 'standard',
    provider: getEngineConfig('standard').provider,
  }), [businessName, offer, platform, platformCreativeGuidance, preset]);

  const ensureAnonymousSession = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return;

    const { data, error: anonError } = await supabase.auth.signInAnonymously();
    if (anonError || !data.user) {
      throw new Error('Start a free account to generate your first ad.');
    }

    await supabase
      .from('profiles')
      .upsert(
        { id: data.user.id, email: data.user.email ?? '', plan: 'free' },
        { onConflict: 'id', ignoreDuplicates: true }
      );
  };

  const handleGenerate = async () => {
    if (!canGenerate || isGenerating) return;
    if (isAnonymousVisitor) {
      const nextAttemptCount = anonymousAttempts + 1;
      setAnonymousAttempts(nextAttemptCount);
      window.localStorage.setItem(anonymousAttemptKey, String(nextAttemptCount));
      trackEvent('quick_start_anonymous_generation_attempted', {
        attempt_count: nextAttemptCount,
        successful_generations: anonymousGenerations,
        limit: anonymousGenerationLimit,
        platform,
      });
    }
    if (anonymousLimitReached) {
      trackEvent('quick_start_signup_prompt_shown', {
        reason: 'anonymous_limit_reached',
        anonymous_generations: anonymousGenerations,
      });
      setError('Nice work. Create a free account to save this ad and keep generating.');
      return;
    }
    if (!enoughCredits) {
      setError('You need more credits before generating this ad.');
      return;
    }

    const abortController = new AbortController();
    generationAbortRef.current = abortController;
    setIsGenerating(true);
    setError('');
    setResult(null);
    setProfileStatus('idle');

    try {
      await ensureAnonymousSession();
      const ad = await fetchGeneratedAds(payload, { signal: abortController.signal });
      if (abortController.signal.aborted || generationAbortRef.current !== abortController) return;
      setResult(ad);
      setGenerationCount((count) => count + 1);
      if (isAnonymousVisitor) {
        const nextAnonymousCount = anonymousGenerations + 1;
        setAnonymousGenerations(nextAnonymousCount);
        window.localStorage.setItem(anonymousGenerationKey, String(nextAnonymousCount));
      }
      await refetch();
      trackEvent('first_ad_generated', {
        source: 'quick_start',
        industry,
        platform,
        credits_used: quickStartCost,
        images_generated: 1,
      });
      trackEvent('credits_used', {
        source: 'quick_start',
        action: 'generate_ad',
        amount: quickStartCost,
      });
      trackEvent('quick_start_generated', {
        industry,
        platform,
        generation_count: generationCount + 1,
        anonymous: isAnonymousVisitor,
      });
      if (generationCount >= 1) {
        trackEvent('repeat_generation_session', {
          source: 'quick_start',
          generation_count: generationCount + 1,
          platform,
        });
      }
    } catch (err) {
      if (abortController.signal.aborted) {
        if (generationAbortRef.current === abortController) {
          setError('Generation stopped. Update your form and try again when you are ready.');
        }
      } else if (err instanceof InsufficientCreditsError) {
        setError('You do not have enough credits for this generation.');
      } else {
        setError(err instanceof Error ? err.message : 'Generation failed. Please try again.');
      }
    } finally {
      if (generationAbortRef.current === abortController) {
        generationAbortRef.current = null;
        setIsGenerating(false);
      }
    }
  };

  const stopGeneration = () => {
    generationAbortRef.current?.abort();
    generationAbortRef.current = null;
    setIsGenerating(false);
    setError('Generation stopped. Update your form and try again when you are ready.');
  };

  const handleSaveProfile = async () => {
    trackEvent('save_clicked', {
      source: 'quick_start',
      anonymous: isAnonymousVisitor,
      platform,
    });
    if (isAnonymousUser) return;
    setProfileStatus('saving');
    const { error: saveError } = await createProfile({
      business_name: businessName.trim(),
      business_type: preset.businessType,
      business_description: payload.businessDescription,
      website: '',
      location: '',
      services_offered: preset.servicesOffered,
      unique_differentiator: `${preset.uniqueDifferentiator}. ${preset.emotionalHook}`,
      default_audience: preset.audience,
      default_tone: preset.tone,
      brand_positioning: preset.brandPositioning,
      logo_url: null,
    });
    if (saveError) {
      setProfileStatus('error');
    } else {
      setSavedAdsCount((count) => count + 1);
      setProfileStatus('saved');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <header className="border-b border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-sm font-extrabold text-stone-900">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500 text-white">
              <i className="ri-flashlight-fill" />
            </span>
            AdForge AI
          </Link>
          <Link
            to={hasUser && !isAnonymousUser ? '/dashboard' : '/signup'}
            onClick={() => {
              if (isAnonymousVisitor) {
                trackEvent('quick_start_signup_clicked', { source: 'header_save' });
              }
            }}
            className="text-sm font-semibold text-stone-500 hover:text-stone-900"
          >
            {hasUser && !isAnonymousUser ? 'Dashboard' : 'Save my ad'}
          </Link>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl gap-6 px-6 py-8 lg:grid-cols-[0.92fr_1.08fr]">
        <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-orange-500">Quick start</p>
              <h1 className="mt-1 text-2xl font-extrabold text-stone-950">Generate your first ad</h1>
              <p className="mt-2 text-sm leading-6 text-stone-500">
                Four inputs, one ad, no dashboard setup required. Required fields are marked.
              </p>
            </div>
            <div className="rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-right">
              <p className="text-xs text-stone-400">Ads remaining</p>
              <p className={`text-sm font-extrabold ${enoughCredits ? 'text-stone-800' : 'text-red-600'}`}>
                {creditsLoading && hasUser ? '...' : adsRemaining}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Business name
                <span className="text-orange-500">*</span>
              </label>
              <input
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
                placeholder="e.g. Summit Roofing Co."
                required
                aria-required="true"
                className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Industry
                <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-extrabold text-stone-400">Recommended</span>
              </label>
              <select
                value={industry}
                onChange={(event) => setIndustry(event.target.value as IndustryKey)}
                className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm font-semibold outline-none transition focus:border-orange-400 focus:bg-white"
              >
                {Object.entries(INDUSTRY_PRESETS).map(([key, item]) => (
                  <option key={key} value={key}>{item.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Offer
                <span className="text-orange-500">*</span>
              </label>
              <input
                value={offer}
                onChange={(event) => setOffer(event.target.value)}
                placeholder="e.g. Free inspection this week"
                required
                aria-required="true"
                className="w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm outline-none transition focus:border-orange-400 focus:bg-white"
              />
            </div>

            <div>
              <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-stone-600">
                Platform
                <span className="text-orange-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {platforms.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setPlatform(item)}
                    className={`rounded-xl border px-3 py-2.5 text-sm font-bold transition ${
                      platform === item
                        ? 'border-orange-500 bg-orange-50 text-orange-700'
                        : 'border-stone-200 bg-stone-50 text-stone-600 hover:border-orange-300'
                    }`}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50 p-4">
            <div className="flex items-center justify-between gap-3 text-sm">
              <span className="font-semibold text-stone-600">1 ad = ~{quickStartCost} credits</span>
              <span className="font-extrabold text-stone-950">{adsRemaining} ad{adsRemaining === 1 ? '' : 's'} remaining</span>
            </div>
            <p className="mt-1 text-xs text-stone-400">
              Generate up to {anonymousGenerationLimit} quick-start ads before signup. Create an account after to save, export, and make more.
            </p>
          </div>

          {anonymousLimitReached && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <div className="flex items-center gap-3">
                <i className="ri-lock-unlock-line text-base" />
                <span className="flex-1">You used your {anonymousGenerationLimit} quick-start ads. Sign up free to keep generating and save your work.</span>
                <Link
                  to="/signup"
                  onClick={() => trackEvent('quick_start_signup_clicked', { source: 'anonymous_limit' })}
                  className="font-bold text-emerald-900 underline underline-offset-2"
                >
                  Sign up
                </Link>
              </div>
            </div>
          )}

          {!enoughCredits && !creditsLoading && (
            <div className="mt-4 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <i className="ri-error-warning-line text-base" />
              <span className="flex-1">Low credits. Add credits to generate without interruption.</span>
              <Link
                to="/pricing"
                onClick={() => trackEvent('upgrade_clicked', { source: 'quick_start_low_credits' })}
                className="font-bold text-amber-900 underline underline-offset-2"
              >
                Upgrade
              </Link>
            </div>
          )}

          {error && (
            <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              if (result) {
                trackEvent('second_variation_clicked', {
                  source: 'primary_button',
                  generation_count: generationCount,
                  anonymous: isAnonymousVisitor,
                });
              }
              handleGenerate();
            }}
            disabled={!canGenerate || isGenerating || anonymousLimitReached || (creditsLoading && hasUser) || !enoughCredits}
            className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-sm font-extrabold transition ${
              canGenerate && enoughCredits && !anonymousLimitReached && !isGenerating && !(creditsLoading && hasUser)
                ? 'bg-gradient-to-r from-orange-500 to-amber-400 text-white hover:from-orange-600 hover:to-amber-500'
                : 'bg-stone-200 text-stone-400'
            }`}
          >
            {isGenerating ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                Generating ad...
              </>
            ) : (
              <>
                <i className="ri-flashlight-fill text-base" />
                {result ? 'Generate Another Variation' : 'Generate My Ad'} - ~{quickStartCost} Credits
              </>
            )}
          </button>
          {!canGenerate && !isGenerating && (
            <p className="mt-2 text-center text-xs font-semibold text-stone-500">
              Add {missingRequiredFields.join(', ')} to enable generation.
            </p>
          )}
          {isGenerating && (
            <button
              type="button"
              onClick={stopGeneration}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white py-3 text-sm font-extrabold text-stone-700 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
            >
              <i className="ri-stop-circle-line text-base" />
              Stop and edit form
            </button>
          )}

          <div className="mt-4 rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-800">
            <div className="flex items-start gap-2">
              <i className="ri-sparkling-2-line mt-0.5" />
              <p>
                Need more variations, premium images, or sharper platform optimization?{' '}
                <Link
                  to="/pricing"
                  onClick={() => trackEvent('upgrade_clicked', { source: 'quick_start_advanced_options' })}
                  className="font-bold underline underline-offset-2"
                >
                  See paid options
                </Link>
              </p>
            </div>
          </div>
        </section>

        <section className="min-h-[620px] rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
          {!result && !isGenerating && (
            <div className="flex h-full min-h-[560px] flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-stone-100 text-stone-400">
                <i className="ri-advertisement-line text-2xl" />
              </div>
              <h2 className="text-xl font-extrabold text-stone-900">Your ad will appear here</h2>
              <p className="mt-2 max-w-sm text-sm leading-6 text-stone-500">
                The industry preset shapes the copy, audience, image direction, and offer framing automatically.
              </p>
            </div>
          )}

          {isGenerating && (
            <div className="flex h-full min-h-[560px] flex-col items-center justify-center text-center">
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-50 text-orange-500">
                <i className="ri-sparkling-2-line text-3xl" />
              </div>
              <div className="w-full max-w-sm space-y-3">
                {progressSteps.map((step, index) => {
                  const active = index === progressIndex;
                  const complete = index < progressIndex;
                  return (
                    <div
                      key={step}
                      className={`flex items-center gap-3 rounded-xl border px-4 py-3 text-left transition ${
                        active
                          ? 'border-orange-300 bg-orange-50 text-orange-800'
                          : complete
                            ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                            : 'border-stone-200 bg-stone-50 text-stone-400'
                      }`}
                    >
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                        complete ? 'bg-emerald-500 text-white' : active ? 'bg-orange-500 text-white' : 'bg-stone-200 text-stone-500'
                      }`}>
                        {complete ? <i className="ri-check-line" /> : index + 1}
                      </span>
                      <span className="text-sm font-extrabold">{step}</span>
                    </div>
                  );
                })}
              </div>
              <div className="mt-5 flex items-center gap-2 rounded-full border border-stone-200 bg-white px-4 py-2 text-xs font-bold text-stone-500 shadow-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-orange-400 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-orange-500" />
                </span>
                Building a {platform} ad for {preset.label.toLowerCase()} buyers
              </div>
              <button
                type="button"
                onClick={stopGeneration}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-xl border border-stone-300 bg-white px-5 py-3 text-sm font-extrabold text-stone-700 transition hover:border-red-300 hover:bg-red-50 hover:text-red-700"
              >
                <i className="ri-stop-circle-line text-base" />
                Stop and edit form
              </button>
            </div>
          )}

          {result && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                    <i className="ri-check-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm font-extrabold text-emerald-950">Your {platform} ad is ready to use</p>
                    <p className="mt-1 text-xs leading-5 text-emerald-800">
                      Saved ads: {savedAdsCount}. Try another angle or test a new hook to find the strongest version.
                    </p>
                  </div>
                </div>
                <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-bold text-emerald-700">
                  Finished asset
                </span>
              </div>

              {imageSource && (
                <div className="overflow-hidden rounded-2xl border border-stone-200 bg-stone-100">
                  <img src={imageSource} alt="Generated ad creative" className="aspect-[4/3] w-full object-cover" />
                </div>
              )}

              <div>
                <p className="mb-2 text-xs font-bold uppercase tracking-wider text-orange-500">{platform} ad</p>
                <h2 className="text-2xl font-extrabold text-stone-950">{firstValue(result.headlines) || firstValue(result.hooks)}</h2>
                <p className="mt-3 text-sm leading-6 text-stone-600">{firstValue(result.primaryText) || firstValue(result.subheadlines)}</p>
                <div className="mt-4 inline-flex items-center rounded-xl bg-stone-950 px-4 py-2 text-sm font-bold text-white">
                  {firstValue(result.ctas) || 'Learn More'}
                </div>
              </div>

              <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
                <p className="text-sm font-extrabold text-stone-900">Why this works</p>
                <div className="mt-3 grid gap-2 text-xs leading-5 text-stone-600 sm:grid-cols-2">
                  <div className="rounded-lg bg-white p-3">
                    <span className="font-bold text-stone-900">Audience fit:</span> It speaks to {preset.audience.toLowerCase()}.
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <span className="font-bold text-stone-900">Hook strength:</span> It taps into {preset.emotionalHook.toLowerCase()}.
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <span className="font-bold text-stone-900">Offer clarity:</span> It frames the next step around {offer.trim()}.
                  </div>
                  <div className="rounded-lg bg-white p-3">
                    <span className="font-bold text-stone-900">Platform fit:</span> {platformCreativeGuidance}
                  </div>
                </div>
              </div>

              {generationCount < 2 ? (
                <div className="rounded-xl border border-orange-100 bg-orange-50 p-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-sparkling-2-line mt-0.5 text-orange-500" />
                    <div>
                      <p className="text-sm font-extrabold text-orange-900">Want a stronger version?</p>
                      <p className="mt-1 text-xs leading-5 text-orange-800">
                        Upgrade for higher-converting ad copy, more realistic premium images, and better platform optimization.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-orange-300 bg-orange-50 p-4 shadow-sm">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-extrabold text-orange-950">Scale the winners faster</p>
                      <p className="mt-1 text-xs leading-5 text-orange-800">
                        Paid plans unlock more credits, higher-converting copy variations, premium realistic images, and deeper {platform} optimization.
                      </p>
                    </div>
                    <Link
                      to="/pricing"
                      onClick={() => trackEvent('upgrade_clicked', { source: 'quick_start_second_generation_prominent' })}
                      className="inline-flex items-center justify-center rounded-lg bg-orange-600 px-4 py-2 text-xs font-extrabold text-white"
                    >
                      See upgrade options
                    </Link>
                  </div>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <button
                  type="button"
                  onClick={() => {
                    trackEvent('second_variation_clicked', {
                      generation_count: generationCount,
                      anonymous: isAnonymousVisitor,
                    });
                    handleGenerate();
                  }}
                  disabled={isGenerating || !enoughCredits || anonymousLimitReached}
                  className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-amber-400 px-3 py-3 text-sm font-extrabold text-white hover:from-orange-600 hover:to-amber-500 disabled:opacity-60"
                >
                  <i className="ri-refresh-line" />
                  Try another angle
                </button>
                {hasUser && !isAnonymousUser ? (
                  <button
                    type="button"
                    onClick={handleSaveProfile}
                    disabled={profileStatus === 'saving' || profileStatus === 'saved'}
                    className="flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm font-extrabold text-emerald-800 hover:border-emerald-300 disabled:opacity-60"
                  >
                    <i className="ri-save-line" />
                    {profileStatus === 'saved' ? 'Saved' : 'Save this ad'}
                  </button>
                ) : (
                  <Link
                    to="/signup"
                    onClick={() => {
                      trackEvent('save_clicked', { source: 'action_bar_save', anonymous: true, platform });
                      trackEvent('quick_start_signup_clicked', { source: 'action_bar_save' });
                    }}
                    className="flex items-center justify-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm font-extrabold text-emerald-800 hover:border-emerald-300"
                  >
                    <i className="ri-save-line" />
                    Save this ad
                  </Link>
                )}
                <Link
                  to={hasUser && !isAnonymousUser ? '/dashboard/export' : '/signup'}
                  onClick={() => {
                    trackEvent('export_clicked', {
                      source: 'quick_start_action_bar',
                      platform,
                      anonymous: isAnonymousVisitor,
                    });
                    if (isAnonymousVisitor) {
                      trackEvent('quick_start_signup_clicked', { source: 'action_bar_export' });
                    }
                  }}
                  className="flex items-center justify-center gap-2 rounded-xl border border-stone-200 bg-white px-3 py-3 text-sm font-extrabold text-stone-700 hover:border-orange-300 hover:text-orange-600"
                >
                  <i className="ri-download-line" />
                  Export {platform}
                </Link>
                <Link
                  to="/pricing"
                  onClick={() => trackEvent('upgrade_clicked', { source: 'quick_start_action_bar' })}
                  className="flex items-center justify-center gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3 py-3 text-sm font-extrabold text-orange-800 hover:border-orange-300"
                >
                  <i className="ri-sparkling-2-line" />
                  Better copy + images
                </Link>
              </div>

              <p className="text-center text-xs text-stone-500">
                Export is labeled for {platform} and ready to adapt for your campaign, post, or ad manager.
              </p>

              {anonymousLimitReached && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-extrabold text-emerald-900">You have {anonymousGenerationLimit} strong starting points</p>
                      <p className="mt-1 text-xs text-emerald-700">Create a free account to save them and keep generating.</p>
                    </div>
                    <Link
                      to="/signup"
                      onClick={() => trackEvent('quick_start_signup_clicked', { source: 'post_limit_prompt' })}
                      className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-xs font-extrabold text-white"
                    >
                      Sign up free
                    </Link>
                  </div>
                </div>
              )}

              {imageSource === '' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                  <i className="ri-image-line mr-2" />
                  Your copy is ready. The image is still being finalized or could not be attached to this result.
                </div>
              )}

              {(isAnonymousUser || !hasUser) && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-extrabold text-emerald-900">Create a free account to keep this ad</p>
                      <p className="mt-1 text-xs text-emerald-700">Save your profile, export the creative, and generate more variations.</p>
                    </div>
                    <Link
                      to="/signup"
                      onClick={() => {
                        trackEvent('save_clicked', { source: 'quick_start_save_prompt', anonymous: true, platform });
                        trackEvent('quick_start_signup_clicked', { source: 'quick_start_save_prompt' });
                      }}
                      className="inline-flex items-center justify-center rounded-lg bg-emerald-600 px-4 py-2 text-xs font-extrabold text-white"
                    >
                      Save my ad
                    </Link>
                  </div>
                </div>
              )}

              {profileStatus === 'idle' && hasUser && !isAnonymousUser && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-extrabold text-emerald-900">Save this business profile?</p>
                      <p className="mt-1 text-xs text-emerald-700">Optional. It makes future ads faster without blocking this result.</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setProfileStatus('skipped')}
                        className="rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-bold text-emerald-700"
                      >
                        Later
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProfile}
                        className="rounded-lg bg-emerald-600 px-3 py-2 text-xs font-bold text-white"
                      >
                        Save Profile
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {profileStatus === 'saving' && <p className="text-sm font-semibold text-stone-500">Saving profile...</p>}
              {profileStatus === 'saved' && <p className="text-sm font-semibold text-emerald-700">Profile saved for your next campaign.</p>}
              {profileStatus === 'error' && <p className="text-sm font-semibold text-red-600">Profile could not be saved. Your ad is still ready.</p>}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
